import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Tooltip, IconButton } from '@mui/material';
import { GridCellCheckboxRenderer, GridColumns, GridRenderCellParams, GridRowParams, GridRowClassNameParams } from '@mui/x-data-grid-pro';
import FileManagerTabBase, { FileRow, FileSystemType } from './FileManagerTabBase';
import { DataLakeDirectoryModel, DataLakeFileModel, Maybe, RequestResult } from '../../gql-types.generated';
import FolderIcon from '@mui/icons-material/Folder';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import UploadIcon from '@mui/icons-material/Upload';
import RefreshIcon from '@mui/icons-material/Refresh';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import PathBreadcrumbs from './PathBreadcrumbs';
import UploadFileDialog from '../dialogs/UploadFileDialog';
import CreateDirectoryDialog from '../dialogs/CreateDirectoryDialog';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { uploadFile, createDirectory } from '../../features/FileManager/FileManagerActions';
import { selectUploadFileStatus, captureUploadFileStatus, clearActionError, selectActionError, selectCreateDirectoryStatus, captureCreateDirectoryStatus } from '../../features/FileManager/FileManagerSlice';
import { setToastConfig } from '../../features/EDIContainer/EDIContainerSlice';
import { ToastSeverity, Viewer } from '../../util/Constants';
import { viewerIsAdmin } from '../../util/ViewerUtility';

interface ActiveDirectoryExplorerProps {
    viewer?: Viewer;
    directory?: DataLakeDirectoryModel;
    onDirectoryClick: (path: string) => void;
    loading?: boolean;
    onRefreshTab: (path: string) => void;
    onRefreshTree: () => void;
}

const ActiveDirectoryExplorer: React.FC<ActiveDirectoryExplorerProps> = (props) => {
    const { viewer, directory, onDirectoryClick, loading, onRefreshTab, onRefreshTree } = props;
    const dispatch = useAppDispatch();
    const [openUploadDialog, setOpenUploadDialog] = useState(false);
    const [openCreateDirectoryDialog, setOpenCreateDirectoryDialog] = useState(false);
    const [searchText, setSearchText] = useState('');

    const uploadStatus = useAppSelector(selectUploadFileStatus);
    const createDirectoryStatus = useAppSelector(selectCreateDirectoryStatus);
    const actionError = useAppSelector(selectActionError);

    const isAdmin = viewerIsAdmin(viewer?.role);

    const refresh = useCallback((includeTree?: boolean) => {
        setSearchText(''); // Reset search text on refresh
        if (directory?.path) {
            onRefreshTab(directory.path);
        }
        if (includeTree) {
            onRefreshTree();
        }
    }, [directory?.path, onRefreshTab, onRefreshTree]);


    useEffect(() => {
        if (uploadStatus?.result === RequestResult.Success) {
            setOpenUploadDialog(false);
            dispatch(setToastConfig({
                message: uploadStatus.message as string,
                severity: ToastSeverity.Success
            }));
            refresh();
            dispatch(captureUploadFileStatus(undefined));
        }
    }, [uploadStatus, refresh]);

    useEffect(() => {
        if (createDirectoryStatus?.result === RequestResult.Success) {
            setOpenCreateDirectoryDialog(false);
            dispatch(setToastConfig({
                message: createDirectoryStatus.message as string,
                severity: ToastSeverity.Success
            }));
            refresh(true);
            dispatch(captureCreateDirectoryStatus(undefined));
        }
    }, [createDirectoryStatus, refresh]);

    // Clear search when directory changes
    useEffect(() => {
        setSearchText('');
    }, [directory?.path]);

    const allRows = useMemo(() => {
        if (!directory) return [];

        return [
            ...(directory.childDirectories?.map((dir: Maybe<DataLakeDirectoryModel>) => ({
                id: dir?.path || '',
                name: dir?.name || '',
                type: FileSystemType.Directory,
                isDirectory: true,
                path: dir?.path || '',
                lastModifiedTime: dir?.lastModifiedTime || '',
                createdTime: dir?.createdTime || '',
                fileSizeDisplay: '', // Directories do not have file size
                fileSize: 0, // Directories do not have file size,
                isDeleteDisabled: dir?.isDeleteDisabled || false
            } as FileRow)) || []),
            ...(directory.dataLakeFileModels?.map((file: Maybe<DataLakeFileModel>) => ({
                id: file?.path || '',
                name: file?.name || '',
                type: FileSystemType.File,
                isDirectory: false,
                path: file?.path || '',
                lastModifiedTime: file?.lastModifiedTime || '',
                createdTime: file?.createdTime || '',
                fileSizeDisplay: file?.fileSize || '',
                fileSize: file?.fileSizeBytes ?? 0,
                isDeleteDisabled: false 
            } as FileRow)) || [])
        ];
    }, [directory]);

    // Filter rows based on search text
    const filteredRows = useMemo(() => {
        if (!searchText.trim()) {
            return allRows;
        }
        
        const searchLower = searchText.toLowerCase();
        return allRows.filter(row => 
            row.name.toLowerCase().includes(searchLower)
        );
    }, [allRows, searchText]);

    // Search change handler
    const handleSearchChange = (newSearchText: string) => {
        setSearchText(newSearchText);
    };

    // Dynamic search placeholder
    const searchPlaceholder = useMemo(() => {
        const directoryName = directory?.name || directory?.path?.split('/').pop() || '';
        return `Search ${directoryName}...`;
    }, [directory?.name, directory?.path]);

    const columns = useMemo<GridColumns>(() => [
        {
            field: 'type',  // This field will handle sorting by name and type
            headerName: 'NAME',
            flex: 2,
            sortable: true,
            valueGetter: (params) => {
                const row = params.row as FileRow;
                return `${row.type}-${row.name}`;
            },
            renderCell: (params: GridRenderCellParams) => {
                const row = params.row as FileRow;
                return (
                    <Box display="flex" alignItems="center" width="100%">
                        <Box mr={1}>
                            {row.isDirectory ? <FolderIcon color='secondary' /> : <InsertDriveFileIcon color='action' />}
                        </Box>
                        <Box sx={{ overflow: 'hidden' }}>
                            <div className='ediDataGridCellOverflowHidden'>{row.name}</div>
                        </Box>
                    </Box>
                );
            }
        },
    ], []);

    const onUploadDialogClose = () => {
        setOpenUploadDialog(false);
        dispatch(clearActionError());
        dispatch(captureUploadFileStatus());
    };

    const onCreateDirectoryDialogClose = () => {
        setOpenCreateDirectoryDialog(false);
        dispatch(clearActionError());
        dispatch(captureCreateDirectoryStatus());
    };    

    const onCreateDirectoryDialogSave = (directoryName: string) => {
        if (directoryName && directory?.path) {
            dispatch(createDirectory(directoryName, directory.path));
        }
    };

    const onUploadDialogSave = (files: Array<{content: string, fileName: string}>) => {
        if (files && files.length > 0) {
            dispatch(uploadFile(files));
        }
    };

    const hasFiles = filteredRows.some((row: FileRow) => !row.isDirectory);
    const isFailedOrArchiveFolder = directory?.path?.endsWith('/Failed') || directory?.path?.endsWith('/Archive');

    const customToolbarBreadcrumbsContent = (
        <PathBreadcrumbs path={directory?.path} onNavigate={onDirectoryClick} />
    );

    const customToolbarButtonContent = (
        <>
            <Tooltip title="Refresh">
                <IconButton
                    onClick={() => refresh(true)}
                    size="small"
                    color="primary"
                    sx={{ mr: 1 }}
                >
                    <RefreshIcon />
                </IconButton>
            </Tooltip>
            {isAdmin && (
                <Tooltip title="Add Directory">
                    <IconButton
                        onClick={() => setOpenCreateDirectoryDialog(true)}
                        size="small"
                        color="primary"
                        sx={{ mr: 1 }}
                    >
                        <CreateNewFolderIcon />
                    </IconButton>
                </Tooltip>
            )}
            {isAdmin && (
                <Tooltip title="Upload Files">
                    <IconButton
                        onClick={() => setOpenUploadDialog(true)}
                        size="small"
                        color="primary"
                        sx={{ mr: 1 }}
                    >
                        <UploadIcon />
                    </IconButton>
                </Tooltip>
            )}
        </>
    );

    if (!directory && !loading) {
        return (
            <Box p={2}>No directory selected. Select one from the Directories Panel</Box>
        );
    }

    return (
        <>
            <FileManagerTabBase
                viewer={viewer}
                showFileActionButtons={hasFiles}
                showReprocessButton={isFailedOrArchiveFolder}
                onRefreshTab={refresh}
                onRefreshTree={onRefreshTree}
                columns={columns}
                rows={filteredRows}
                customToolbarBreadcrumbsContent={customToolbarBreadcrumbsContent}
                customToolbarButtonContent={customToolbarButtonContent}
                loading={loading}
                checkboxSelection={hasFiles}
                defaultSortField="type"
                defaultSortDirection="asc"
                overlayErrorTitle="Unable to load the Directory"
                noResultsMessage={searchText ? "Clear or modify the Search criteria." : "Select a different directory to view files."}
                searchPlaceholder={searchPlaceholder}
                onSearchChange={handleSearchChange}
                searchValue={searchText}
                minSearchLength={0}
                gridProps={{
                    isRowSelectable: (params: GridRowParams) => !params.row.isDirectory,
                    getRowClassName: (params: GridRowClassNameParams) =>
                        params.row.isDirectory ? 'directory-row' : 'file-row',
                    onRowClick: (params) => {
                        if (params.row.isDirectory && params.row.path) {
                            onDirectoryClick(params.row.path);
                        }
                    },
                }}
                checkboxColumnProps={{
                    width: 50,
                    renderCell: (params: GridRenderCellParams) => {
                        if (params.row.isDirectory) {
                            return ''; // Render an empty cell to hide checkbox for directories
                        }
                        return <GridCellCheckboxRenderer {...params} />;
                    }
                }}
            />
            <UploadFileDialog
                isOpen={openUploadDialog}
                currentPath={directory?.path || '/'}
                maxFiles={150} // limiting to 150 files for potential performance issues
                onClose={onUploadDialogClose}
                onSave={onUploadDialogSave}
                error={actionError}
            />
            <CreateDirectoryDialog
                isOpen={openCreateDirectoryDialog}
                onClose={onCreateDirectoryDialogClose}
                onSave={onCreateDirectoryDialogSave}
                error={actionError}
            />
        </>
    );
};

export default ActiveDirectoryExplorer;
